import {Vector2} from 'three';
import type {Object3D} from 'three';

import {
    isSketchGroup,
    isSketchParabolaObject,
    isSketchSplineObject,
    isSketchStyleSplineObject,
} from '../../../mesh/guards';
import type {AbstractSketchEdgeObject} from '../../../mesh/sketch/AbstractSketchEdgeObject';
import type {SketchGroup, SketchParabolaObject, SketchSplineObject} from '../../../mesh/sketch';
import type {ReferencePlane} from '../../../reference';
import {SketchEntityEquidistantOffset} from '../../offset/SketchEntityEquidistantOffset';
import {SplineOffsetGeometryService} from '../../offset/SplineOffsetGeometryService';
import type {SketchDimensionRepository} from '../repository/SketchDimensionRepository';

/**
 * 曲线偏移派生同步服务端口。
 */
export interface CurveOffsetDerivedSyncServicePort {
    /** 尺寸仓库（`curve-offset-distance` 尺寸是源/等距样条对的持久纽带）。 */
    readonly repository: SketchDimensionRepository;
    /** 按草图持久 ID 解析草图组。 */
    readonly resolveSketchGroup: (sketchId: string) => SketchGroup | null;
    /** 输出用户可见提示（自相交保护等）。 */
    readonly notifyNotice?: (message: string, level: 'warning') => void;
    /** 请求渲染刷新。 */
    readonly requestRender?: () => void;
}

/**
 * 等距样条派生同步服务。
 *
 * 职责（单一）：
 * 1. 以 `curve-offset-distance` 尺寸为纽带，把源曲线与等距样条配成偏移对——
 *    源曲线支持样条/样式样条与抛物线（抛物线等距方案A输出样条）；
 * 2. 任一曲线被编辑（求解写回）后，用最新曲线点按名义距离重算另一侧并静默写回——
 *    源被编辑 → 等距样条 = offset(源, +d)；等距被编辑 → 源样条 = offset(等距, −d)；
 *    抛物线源无法从等距样条反推，只支持"源为权威侧"的前向同步；
 * 3. 订阅曲线异步重建完成事件，在权威采样点落地后再次同步，消除滞后一拍；
 * 4. 自相交保护：偏移距离导致"打结"时保留上一有效形状并提示。
 */
export class CurveOffsetDerivedSyncService {
    /** 几何计算服务（与创建路径共用同一 miter 偏移口径）。 */
    private readonly geometryService = new SplineOffsetGeometryService();

    /** 正在同步的尺寸 ID（重入防护，防双向 ping-pong）。 */
    private readonly syncingDimensionIds = new Set<string>();

    /** 尺寸 ID → 双曲线重建监听注销函数（dispose 统一清理）。 */
    private readonly pairListenerCleanups = new Map<string, Array<() => void>>();

    /**
     * @param port 宿主端口。
     */
    public constructor(private readonly port: CurveOffsetDerivedSyncServicePort) {
    }

    /**
     * 释放全部曲线重建监听。
     */
    public dispose(): void {
        this.pairListenerCleanups.forEach((cleanups) => {
            cleanups.forEach((cleanup) => cleanup());
        });
        this.pairListenerCleanups.clear();
    }

    /**
     * 对一次求解写回波及的边执行派生同步。
     *
     * 说明：仅处理"样条对偏移尺寸"涉及的边；被编辑侧保持不动，另一侧重算。
     *
     * @param affectedEdges 求解写回波及的边集合。
     */
    public synchronizeAffectedEdges(affectedEdges: Iterable<AbstractSketchEdgeObject>): void {
        // 按草图分组：解析受影响边所属草图，避免对无关草图做尺寸扫描
        const sketchIds = new Set<string>();
        const affectedUuids = new Set<string>();

        for (const edge of affectedEdges) {
            affectedUuids.add(edge.uuid);
            const group = this.resolveSketchGroupOf(edge);

            if (group) {
                sketchIds.add(group.persistentId);
            }
        }

        sketchIds.forEach((sketchId) => {
            this.synchronizeSketch(sketchId, affectedUuids);
        });
    }

    /**
     * 尺寸领域变更（修改名义值等）后，按"源样条为权威侧"同步全部样条偏移对。
     *
     * 说明：修改等距距离尺寸值时没有几何写回，派生同步不会经
     * {@link synchronizeAffectedEdges} 触发；这里把源样条作为权威侧，
     * 等距样条按新名义值整条重算（对齐 SW：改偏移尺寸移动等距实体）。
     *
     * @param sketchId 草图持久 ID。
     */
    public synchronizeAfterDimensionChange(sketchId: string): void {
        this.forEachActiveSplineOffsetPair(sketchId, (pair) => {
            this.synchronizePair(
                pair.dimensionId,
                pair.source,
                pair.offset,
                pair.nominalValue,
                pair.directionSign,
                true,
            );
        });
    }

    /**
     * 同步单个草图内的全部样条偏移对。
     *
     * @param sketchId 草图持久 ID。
     * @param affectedUuids 本次写回波及的边 uuid。
     */
    private synchronizeSketch(sketchId: string, affectedUuids: ReadonlySet<string>): void {
        this.forEachActiveSplineOffsetPair(sketchId, (pair) => {
            const sourceAffected = affectedUuids.has(pair.source.uuid);
            const offsetAffected = affectedUuids.has(pair.offset.uuid);

            if (!sourceAffected && !offsetAffected) {
                return;
            }

            this.synchronizePair(
                pair.dimensionId,
                pair.source,
                pair.offset,
                pair.nominalValue,
                pair.directionSign,
                sourceAffected,
            );
        });
    }

    /**
     * 遍历草图内全部活跃的曲线偏移对并回调。
     *
     * @param sketchId 草图持久 ID。
     * @param visitor 配对回调。
     */
    private forEachActiveSplineOffsetPair(
        sketchId: string,
        visitor: (pair: {
            readonly dimensionId: string;
            readonly source: SketchSplineObject | SketchParabolaObject;
            readonly offset: SketchSplineObject;
            readonly nominalValue: number;
            readonly directionSign: 1 | -1;
        }) => void,
    ): void {
        const group = this.port.resolveSketchGroup(sketchId);

        if (!group) {
            return;
        }

        this.port.repository.listBySketch(sketchId).forEach((dimension) => {
            const snapshot = dimension.toSnapshot();

            if (snapshot.dimensionKind !== 'curve-offset-distance'
                || snapshot.role !== 'driving'
                || snapshot.suppressed
                || snapshot.solveState === 'dangling') {
                return;
            }

            const sourceReference = snapshot.references[0];
            const offsetReference = snapshot.references[1];

            if (!sourceReference || !offsetReference) {
                return;
            }

            // 多实体组合链由 OffsetChainDerivedSyncService 整链重算（含延伸/相交处理），
            // 本服务只处理单实体偏移对：若用未处理的偏移覆盖链首实体，会丢掉
            // 链服务的整链写回结果（时序上曲线派生重算在链服务之后执行）。
            const chainRecord = snapshot.metadata?.offsetChain;

            if (chainRecord && chainRecord.sourceEntityIds.length > 1) {
                return;
            }

            const source = this.resolveSourceCurve(group, sourceReference.entityId);
            const offset = this.resolveSpline(group, offsetReference.entityId);

            if (!source || !offset) {
                return;
            }

            // 首次遇到该配对时挂接双曲线重建监听（Worker 权威采样点落地后二次同步）
            this.ensurePairListeners(sketchId, snapshot.dimensionId, source, offset);
            visitor({
                dimensionId: snapshot.dimensionId,
                source,
                offset,
                nominalValue: snapshot.parameter.nominalValue,
                directionSign: snapshot.branch.directionSign ?? 1,
            });
        });
    }

    /**
     * 执行一对曲线的派生同步。
     *
     * @param dimensionId 尺寸 ID（重入防护键）。
     * @param source 源曲线（样条/样式样条/抛物线）。
     * @param offset 等距样条。
     * @param nominalDistance 名义偏移距离（米）。
     * @param directionSign 偏移方向分支。
     * @param editedIsSource 被编辑侧是否为源曲线。
     */
    private synchronizePair(
        dimensionId: string,
        source: SketchSplineObject | SketchParabolaObject,
        offset: SketchSplineObject,
        nominalDistance: number,
        directionSign: 1 | -1,
        editedIsSource: boolean,
    ): void {
        if (this.syncingDimensionIds.has(dimensionId)) {
            return;
        }

        // 抛物线源 + 样条偏移：走创建同口径的抛物线重算路径（仅支持源权威方向）
        if (isSketchParabolaObject(source)) {
            this.synchronizeParabolaSourcePair(
                dimensionId,
                source,
                offset,
                nominalDistance,
                directionSign,
                editedIsSource,
            );
            return;
        }

        const base = editedIsSource ? source : offset;
        const target = editedIsSource ? offset : source;
        // 源 → 等距：沿创建方向偏移；等距 → 源：沿反方向偏移（miter 偏移双向可逆）
        const isReverse = editedIsSource
            ? directionSign !== 1
            : directionSign === 1;
        const baseCurvePoints = base.getCurvePoints();

        if (baseCurvePoints.length < 2) {
            return;
        }

        const geometry = this.geometryService.computeOffset(
            baseCurvePoints,
            base.referencePlane,
            nominalDistance * 1000,
            isReverse,
            target.getControlPoints().length,
        );

        if (!geometry) {
            this.port.notifyNotice?.(
                '等距距离导致样条自相交，保留上一有效形状。',
                'warning',
            );
            return;
        }

        const targetControlPoints = target.getControlPoints();

        if (targetControlPoints.length !== geometry.controlPointIndices.length) {
            this.port.notifyNotice?.(
                '等距样条控制点数量与几何结果不一致，跳过同步。',
                'warning',
            );
            return;
        }

        this.syncingDimensionIds.add(dimensionId);
        try {
            // 静默写回控制点（不换点对象，保持身份与持久化稳定）
            const worldOffsetPoints = geometry.offsetLocalPoints.map((point) => (
                target.referencePlane.localToWorld(point)
            ));
            geometry.controlPointIndices.forEach((index, controlIndex) => {
                targetControlPoints[controlIndex]!.syncPositionSilently(
                    worldOffsetPoints[index]!.clone(),
                );
            });
            // 曲线点列以同步计算结果为权威（与创建路径同一几何口径）
            target.updateCurvePoints(worldOffsetPoints);
        } finally {
            this.syncingDimensionIds.delete(dimensionId);
        }

        this.port.requestRender?.();
    }

    /**
     * 执行"抛物线源 + 样条偏移"对的派生同步。
     *
     * 说明：
     * 1. 与创建路径同一口径调用 {@link SketchEntityEquidistantOffset.isometricSketchParabolaObject}
     *    重算等距样条，再静默写回现有偏移样条（不换对象、不换 persistentId）；
     * 2. 抛物线源无法从等距样条反推，仅支持"源为权威侧"的前向同步；
     *    直接编辑等距样条侧时跳过，保留当前形状；
     * 3. 自相交/退化保护：两侧分支都失败时保留上一有效形状并提示。
     *
     * @param dimensionId 尺寸 ID（重入防护键）。
     * @param source 源抛物线。
     * @param offset 等距样条。
     * @param nominalDistance 名义偏移距离（米）。
     * @param directionSign 偏移方向分支（创建路径测量口径）。
     * @param editedIsSource 被编辑侧是否为源曲线。
     */
    private synchronizeParabolaSourcePair(
        dimensionId: string,
        source: SketchParabolaObject,
        offset: SketchSplineObject,
        nominalDistance: number,
        directionSign: 1 | -1,
        editedIsSource: boolean,
    ): void {
        if (this.syncingDimensionIds.has(dimensionId)) {
            return;
        }

        // 抛物线源无法从等距样条反推：反向编辑保留双方当前形状
        if (!editedIsSource) {
            return;
        }

        const refPlane = source.referencePlane;
        // 核心单位米 → 创建路径口径毫米
        const distanceMm = nominalDistance * 1000;
        const candidate = this.resolveParabolaOffsetCandidate(source, distanceMm, directionSign, refPlane);

        if (!candidate) {
            this.port.notifyNotice?.(
                '等距距离导致抛物线偏移自相交或退化，保留上一有效形状。',
                'warning',
            );
            return;
        }

        const targetControlPoints = offset.getControlPoints();
        const candidateControlPoints = candidate.getControlPoints();

        if (targetControlPoints.length !== candidateControlPoints.length) {
            this.port.notifyNotice?.(
                '等距样条控制点数量与几何结果不一致，跳过同步。',
                'warning',
            );
            this.disposeTransientSpline(candidate);
            return;
        }

        this.syncingDimensionIds.add(dimensionId);
        try {
            // 静默写回控制点（不换点对象，保持身份与持久化稳定）
            candidateControlPoints.forEach((point, index) => {
                targetControlPoints[index]!.syncPositionSilently(point.getPosition().clone());
            });
            // 曲线点列以重算结果为权威（与创建路径同一几何口径）
            offset.updateCurvePoints(candidate.getCurvePoints().map((point) => point.clone()));
            // 控制点静默写回后必须按"参数尺寸写回"同口径收口：
            // 1. 立即按新曲线点刷新折线几何（updateCurvePoints 已做，这里兜底派生视觉）；
            // 2. 递增重建版本号作废在途的旧 Worker 重建结果，并按新控制点重新请求
            //    权威重建——否则此前按旧控制点快照发出的重建会把曲线覆盖回旧位置，
            //    表现为"控制点已移动、曲线仍停在原地，拖一下才刷新"。
            offset.refreshGeometryOnly();
        } finally {
            this.syncingDimensionIds.delete(dimensionId);
            // 候选样条未挂场景，写回后释放几何（材质来自共享工厂，不 dispose）
            this.disposeTransientSpline(candidate);
        }

        this.port.requestRender?.();
    }

    /**
     * 按尺寸方向分支解析与创建同侧的抛物线等距样条候选。
     *
     * 说明：抛物线偏移的默认方向由射线法决定（未必等于样条的"顺时针 90°"默认），
     * `directionSign` 记录的是创建路径"首采样段法向"测量口径；因此先按
     * `directionSign` 对应的分支重算，再以同一口径测量候选结果，符号不一致时
     * 改用另一侧分支——保证与创建时落在同一边。
     *
     * @param source 源抛物线。
     * @param distanceMm 偏移距离（毫米）。
     * @param directionSign 尺寸方向分支。
     * @param refPlane 草图参考面。
     * @returns 与创建同侧的等距样条候选；两侧都失败返回 null。
     */
    private resolveParabolaOffsetCandidate(
        source: SketchParabolaObject,
        distanceMm: number,
        directionSign: 1 | -1,
        refPlane: ReferencePlane,
    ): SketchSplineObject | null {
        // 样条口径下 directionSign +1=正向、−1=反向，作为首选分支
        const preferredReverse = directionSign !== 1;

        for (const isReverse of [preferredReverse, !preferredReverse]) {
            const candidate = SketchEntityEquidistantOffset.isometricSketchParabolaObject(
                source,
                distanceMm,
                isReverse,
                refPlane,
            );

            if (!candidate) {
                continue;
            }

            const measuredSign = this.measureParabolaOffsetSideSign(source, candidate, refPlane);

            // 测量不可用（退化）时信任首选分支；符号一致即命中
            if (measuredSign === null || measuredSign === directionSign) {
                return candidate;
            }

            this.disposeTransientSpline(candidate);
        }

        return null;
    }

    /**
     * 按创建路径同一口径测量抛物线偏移候选的方向符号。
     *
     * 口径与 `SketchOffsetController.applySplineOffsetDistanceDimension` 一致：
     * 等距样条首曲线点相对源首采样段"顺时针 90°"法向的投影方向。
     *
     * @param source 源抛物线。
     * @param candidate 等距样条候选。
     * @param refPlane 草图参考面。
     * @returns 方向符号；几何退化无法测量时返回 null。
     */
    private measureParabolaOffsetSideSign(
        source: SketchParabolaObject,
        candidate: SketchSplineObject,
        refPlane: ReferencePlane,
    ): 1 | -1 | null {
        const sourcePoints = source.getCurvePoints();
        const candidatePoints = candidate.getCurvePoints();

        if (sourcePoints.length < 2 || candidatePoints.length < 1) {
            return null;
        }

        const first = refPlane.worldToLocal(sourcePoints[0]!);
        const second = refPlane.worldToLocal(sourcePoints[1]!);
        const offsetFirst = refPlane.worldToLocal(candidatePoints[0]!);
        const segment = new Vector2().subVectors(second, first);
        const segmentLength = segment.length();

        if (segmentLength < 1e-12) {
            return null;
        }

        const normalX = segment.y / segmentLength;
        const normalY = -segment.x / segmentLength;
        const signedDistance = (offsetFirst.x - first.x) * normalX + (offsetFirst.y - first.y) * normalY;

        return signedDistance >= 0 ? 1 : -1;
    }

    /**
     * 释放临时等距样条几何（未挂场景，只释放 BufferGeometry；材质来自共享工厂）。
     *
     * @param spline 临时样条。
     */
    private disposeTransientSpline(spline: SketchSplineObject): void {
        const geometry = (spline as unknown as {geometry?: {dispose?: () => void}}).geometry;

        geometry?.dispose?.();
    }

    /**
     * 首次遇到配对时挂接双曲线重建完成监听。
     *
     * 说明：开放插值样条的权威曲线由 Worker 异步重采样产生；监听落地后
     * 以"该侧为被编辑侧"的角色重新同步，消除滞后一拍。抛物线源没有
     * 曲线重建事件（且不参与反向同步），只挂接等距样条侧监听。
     *
     * @param sketchId 草图持久 ID。
     * @param dimensionId 尺寸 ID。
     * @param source 源曲线。
     * @param offset 等距样条。
     */
    private ensurePairListeners(
        sketchId: string,
        dimensionId: string,
        source: SketchSplineObject | SketchParabolaObject,
        offset: SketchSplineObject,
    ): void {
        if (this.pairListenerCleanups.has(dimensionId)) {
            return;
        }

        const cleanups: Array<() => void> = [];
        const attachListener = (
            spline: SketchSplineObject,
            editedIsSource: boolean,
        ): void => {
            const removeListener = typeof spline.addCurveRebuiltListener === 'function'
                ? spline.addCurveRebuiltListener(() => {
                    const group = this.port.resolveSketchGroup(sketchId);

                    if (!group) {
                        return;
                    }
                    const freshSource = this.resolveSourceCurve(group, source.persistentId);
                    const freshOffset = this.resolveSpline(group, offset.persistentId);

                    if (freshSource && freshOffset) {
                        const dimension = this.port.repository.get(sketchId, dimensionId);
                        const snapshot = dimension?.toSnapshot();

                        if (snapshot && snapshot.role === 'driving' && !snapshot.suppressed) {
                            this.synchronizePair(
                                dimensionId,
                                freshSource,
                                freshOffset,
                                snapshot.parameter.nominalValue,
                                snapshot.branch.directionSign ?? 1,
                                editedIsSource,
                            );
                        }
                    }
                })
                : null;

            if (removeListener) {
                cleanups.push(removeListener);
            }
        };

        // 抛物线源没有曲线重建事件，也不参与反向同步，只挂接等距样条侧
        if (isSketchSplineObject(source) || isSketchStyleSplineObject(source)) {
            attachListener(source as SketchSplineObject, true);
        }
        attachListener(offset, false);
        this.pairListenerCleanups.set(dimensionId, cleanups);
    }

    /**
     * 按持久 ID 解析样条实体。
     *
     * @param group 草图组。
     * @param entityId 实体持久 ID。
     * @returns 样条对象；无法解析或类型不匹配返回 null。
     */
    private resolveSpline(group: SketchGroup, entityId: string): SketchSplineObject | null {
        const target = group.getObjectByPersistentId(entityId);

        if (isSketchSplineObject(target)) {
            return target;
        }
        // 样式样条具有与普通样条一致的曲线点/控制点协议，可直接参与派生同步
        if (isSketchStyleSplineObject(target)) {
            return target as unknown as SketchSplineObject;
        }

        return null;
    }

    /**
     * 按持久 ID 解析偏移对源曲线实体。
     *
     * @param group 草图组。
     * @param entityId 实体持久 ID。
     * @returns 源曲线对象（样条/样式样条/抛物线）；无法解析或类型不匹配返回 null。
     */
    private resolveSourceCurve(
        group: SketchGroup,
        entityId: string,
    ): SketchSplineObject | SketchParabolaObject | null {
        const target = group.getObjectByPersistentId(entityId);

        if (isSketchSplineObject(target) || isSketchStyleSplineObject(target)) {
            return target as SketchSplineObject;
        }
        // 抛物线等距（方案A）输出样条：源侧仍是抛物线，偏移侧按样条处理
        if (isSketchParabolaObject(target)) {
            return target;
        }

        return null;
    }

    /**
     * 沿父链解析对象所属草图组。
     *
     * @param object 运行时对象。
     * @returns 草图组；不属于任何草图组返回 null。
     */
    private resolveSketchGroupOf(object: Object3D | null): SketchGroup | null {
        let current: Object3D | null = object;

        while (current) {
            if (isSketchGroup(current)) {
                return current;
            }
            current = current.parent;
        }

        return null;
    }
}
